import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Home, Search, Bed, PoundSterling, MapPin, Calendar, 
  CheckCircle2, ArrowRight, ArrowLeft, Loader2, Building,
  User, Phone, Mail
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PropertySearch = () => {
  const { toast } = useToast();
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    // Step 1: What are you looking for?
    looking_for: '', // rent or sale
    property_type: '',
    
    // Step 2: Requirements
    min_bedrooms: 1,
    max_bedrooms: 3,
    min_budget: '',
    max_budget: '',
    preferred_areas: [],
    
    // Step 3: Contact & Move-in
    name: '',
    email: '',
    phone: '',
    move_in_date: '',
    additional_requirements: ''
  });

  const propertyTypes = [
    { id: 'flat', label: isArabic ? 'شقة' : 'Flat / Apartment', icon: Building },
    { id: 'house', label: isArabic ? 'منزل' : 'House', icon: Home },
    { id: 'studio', label: isArabic ? 'استوديو' : 'Studio', icon: Building },
    { id: 'room', label: isArabic ? 'غرفة' : 'Room / HMO', icon: Home },
  ];

  const areas = [
    'Harrow', 'Wembley', 'Stanmore', 'Edgware', 'Pinner', 
    'Kenton', 'Northwick Park', 'Rayners Lane', 'Wealdstone', 'Other'
  ];

  const handleAreaToggle = (area) => {
    setFormData(prev => ({
      ...prev,
      preferred_areas: prev.preferred_areas.includes(area)
        ? prev.preferred_areas.filter(a => a !== area)
        : [...prev.preferred_areas, area]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/property-seekers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          min_budget: parseFloat(formData.min_budget) || 0,
          max_budget: parseFloat(formData.max_budget) || 0
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast({
          title: isArabic ? "تم الإرسال بنجاح!" : "Submitted Successfully!",
          description: isArabic ? "سنتواصل معك قريباً" : "We'll be in touch shortly"
        });
      } else {
        const error = await response.json();
        toast({
          title: "Error",
          description: error.detail || "Failed to submit",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong",
        variant: "destructive"
      });
    }
    
    setLoading(false);
  };

  const canProceed = () => {
    if (step === 1) return formData.looking_for && formData.property_type;
    if (step === 2) return formData.max_budget;
    if (step === 3) return formData.name && formData.email && formData.phone;
    return false;
  };

  if (submitted) {
    return (
      <>
        <Header />
        <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-xl mx-auto px-4">
            <Card className="border-none shadow-xl">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-[#404040] mb-4">
                  {isArabic ? 'شكراً لك!' : 'Thank You!'}
                </h2>
                <p className="text-gray-600 mb-6">
                  {isArabic 
                    ? 'تم تسجيل طلبك بنجاح. سيتواصل معك أحد أعضاء فريقنا قريباً لمساعدتك في العثور على عقارك المثالي.'
                    : 'Your property search request has been submitted. One of our team members will contact you shortly to help you find your perfect property.'
                  }
                </p>
                <div className="p-4 bg-[#FF8C42]/10 rounded-lg mb-6">
                  <p className="text-sm text-[#FF8C42]">
                    {isArabic 
                      ? `نحن نبحث عن: ${formData.property_type} ${formData.looking_for === 'rent' ? 'للإيجار' : 'للبيع'}`
                      : `Looking for: ${formData.property_type} to ${formData.looking_for}`
                    }
                  </p>
                </div>
                <Button 
                  onClick={() => window.location.href = '/'}
                  className="bg-[#FF8C42] hover:bg-[#FF6B35]"
                >
                  {isArabic ? 'العودة للرئيسية' : 'Back to Home'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen pt-24 pb-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-2xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#FF8C42] rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-[#404040]">
              {isArabic ? 'ابحث عن عقارك' : 'Find Your Property'}
            </h1>
            <p className="text-gray-600 mt-2">
              {isArabic 
                ? 'أخبرنا بما تبحث عنه وسنساعدك في إيجاده'
                : 'Tell us what you\'re looking for and we\'ll help you find it'
              }
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-center mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= s ? 'bg-[#FF8C42] text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-1 mx-2 ${step > s ? 'bg-[#FF8C42]' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>

          <Card className="border-none shadow-xl">
            <CardContent className="p-8">
              {/* Step 1: Property Type */}
              {step === 1 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <h2 className="text-xl font-bold text-[#404040]">
                      {isArabic ? 'ماذا تبحث عنه؟' : 'What are you looking for?'}
                    </h2>
                  </div>

                  {/* Rent or Buy */}
                  <div>
                    <Label className="text-sm text-gray-600 mb-3 block">
                      {isArabic ? 'نوع البحث' : 'I want to'}
                    </Label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, looking_for: 'rent'})}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          formData.looking_for === 'rent' 
                            ? 'border-[#FF8C42] bg-[#FF8C42]/10' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Home className={`w-8 h-8 mx-auto mb-2 ${
                          formData.looking_for === 'rent' ? 'text-[#FF8C42]' : 'text-gray-400'
                        }`} />
                        <p className="font-medium">{isArabic ? 'إيجار' : 'Rent'}</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, looking_for: 'sale'})}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          formData.looking_for === 'sale' 
                            ? 'border-[#FF8C42] bg-[#FF8C42]/10' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <PoundSterling className={`w-8 h-8 mx-auto mb-2 ${
                          formData.looking_for === 'sale' ? 'text-[#FF8C42]' : 'text-gray-400'
                        }`} />
                        <p className="font-medium">{isArabic ? 'شراء' : 'Buy'}</p>
                      </button>
                    </div>
                  </div>

                  {/* Property Type */}
                  <div>
                    <Label className="text-sm text-gray-600 mb-3 block">
                      {isArabic ? 'نوع العقار' : 'Property Type'}
                    </Label>
                    <div className="grid grid-cols-2 gap-3">
                      {propertyTypes.map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => setFormData({...formData, property_type: type.id})}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            formData.property_type === type.id 
                              ? 'border-[#FF8C42] bg-[#FF8C42]/10' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <type.icon className={`w-6 h-6 mx-auto mb-2 ${
                            formData.property_type === type.id ? 'text-[#FF8C42]' : 'text-gray-400'
                          }`} />
                          <p className="text-sm font-medium">{type.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Requirements */}
              {step === 2 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <h2 className="text-xl font-bold text-[#404040]">
                      {isArabic ? 'متطلباتك' : 'Your Requirements'}
                    </h2>
                  </div>

                  {/* Bedrooms */}
                  <div>
                    <Label className="text-sm text-gray-600 mb-3 block">
                      {isArabic ? 'عدد غرف النوم' : 'Bedrooms'}
                    </Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Min</Label>
                        <select
                          value={formData.min_bedrooms}
                          onChange={(e) => setFormData({...formData, min_bedrooms: parseInt(e.target.value)})}
                          className="w-full p-3 border rounded-lg"
                        >
                          {[0, 1, 2, 3, 4, 5].map(n => (
                            <option key={n} value={n}>{n === 0 ? 'Studio' : n}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Max</Label>
                        <select
                          value={formData.max_bedrooms}
                          onChange={(e) => setFormData({...formData, max_bedrooms: parseInt(e.target.value)})}
                          className="w-full p-3 border rounded-lg"
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                            <option key={n} value={n}>{n}+</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Budget */}
                  <div>
                    <Label className="text-sm text-gray-600 mb-3 block">
                      {isArabic ? 'الميزانية (£)' : `Budget (£${formData.looking_for === 'rent' ? ' per month' : ''})`}
                    </Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Min</Label>
                        <Input
                          type="number"
                          placeholder={formData.looking_for === 'rent' ? '500' : '100000'}
                          value={formData.min_budget}
                          onChange={(e) => setFormData({...formData, min_budget: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Max</Label>
                        <Input
                          type="number"
                          placeholder={formData.looking_for === 'rent' ? '2000' : '500000'}
                          value={formData.max_budget}
                          onChange={(e) => setFormData({...formData, max_budget: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Preferred Areas */}
                  <div>
                    <Label className="text-sm text-gray-600 mb-3 block">
                      {isArabic ? 'المناطق المفضلة' : 'Preferred Areas'}
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {areas.map((area) => (
                        <button
                          key={area}
                          type="button"
                          onClick={() => handleAreaToggle(area)}
                          className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                            formData.preferred_areas.includes(area)
                              ? 'bg-[#FF8C42] text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {area}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Contact Details */}
              {step === 3 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <h2 className="text-xl font-bold text-[#404040]">
                      {isArabic ? 'معلومات الاتصال' : 'Contact Details'}
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">
                      {isArabic ? 'سنتواصل معك بأفضل العروض' : 'We\'ll contact you with the best options'}
                    </p>
                  </div>

                  <div>
                    <Label className="flex items-center gap-2">
                      <User className="w-4 h-4" /> {isArabic ? 'الاسم الكامل' : 'Full Name'} *
                    </Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder={isArabic ? 'أدخل اسمك' : 'Enter your name'}
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4" /> {isArabic ? 'البريد الإلكتروني' : 'Email'} *
                    </Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="your@email.com"
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4" /> {isArabic ? 'رقم الهاتف' : 'Phone Number'} *
                    </Label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="07700 123456"
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> {isArabic ? 'تاريخ الانتقال المفضل' : 'Preferred Move-in Date'}
                    </Label>
                    <Input
                      type="date"
                      value={formData.move_in_date}
                      onChange={(e) => setFormData({...formData, move_in_date: e.target.value})}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>{isArabic ? 'متطلبات إضافية' : 'Additional Requirements'}</Label>
                    <Textarea
                      value={formData.additional_requirements}
                      onChange={(e) => setFormData({...formData, additional_requirements: e.target.value})}
                      placeholder={isArabic 
                        ? 'أي متطلبات خاصة؟ (مثل: حديقة، موقف سيارات، حيوانات أليفة)'
                        : 'Any special requirements? (e.g., garden, parking, pets allowed)'
                      }
                      className="mt-1"
                      rows={3}
                    />
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                {step > 1 ? (
                  <Button 
                    variant="outline" 
                    onClick={() => setStep(step - 1)}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {isArabic ? 'السابق' : 'Back'}
                  </Button>
                ) : (
                  <div />
                )}
                
                {step < 3 ? (
                  <Button 
                    onClick={() => setStep(step + 1)}
                    disabled={!canProceed()}
                    className="bg-[#FF8C42] hover:bg-[#FF6B35]"
                  >
                    {isArabic ? 'التالي' : 'Next'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit}
                    disabled={!canProceed() || loading}
                    className="bg-[#FF8C42] hover:bg-[#FF6B35]"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    {isArabic ? 'إرسال' : 'Submit'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </>
  );
};
